/**
 * What is still open in an exit's scope (SPEC §9.7 "verify flat"; owner 2026-09-27: show progress
 * until the gateway confirms the account(s) are flat, and name what is left after the deadline).
 * Pure, from the replay-safe trading state; the gateway's own `FLATTEN_INCOMPLETE` notices are
 * shown as well.
 */
import type { TradingState } from "./state.ts";
import { isWorkingOrder } from "./state.ts";

export type ExitScope = {
  accounts: readonly string[];
  /** null = every symbol of those accounts. */
  symbol: string | null;
};

export type OpenPosition = { accountId: string; symbol: string; netQty: number };

export type AccountExposure = {
  accountId: string;
  positions: OpenPosition[];
  workingOrders: number;
};

/** Per account in scope: open positions and working orders (the Flatten ALL confirm lists this). */
export function exposureOf(
  s: Pick<TradingState, "positions" | "orders">,
  scope: ExitScope,
): AccountExposure[] {
  return scope.accounts.map((accountId) => {
    const positions: OpenPosition[] = [];
    for (const p of s.positions.values()) {
      if (p.accountId !== accountId || p.netQty === 0) continue;
      if (scope.symbol !== null && p.symbol !== scope.symbol) continue;
      positions.push({ accountId, symbol: p.symbol, netQty: p.netQty });
    }
    positions.sort((a, b) => a.symbol.localeCompare(b.symbol));
    let workingOrders = 0;
    for (const o of s.orders.values()) {
      if (o.accountId !== accountId || !isWorkingOrder(o)) continue;
      if (scope.symbol !== null && o.symbol !== scope.symbol) continue;
      workingOrders++;
    }
    return { accountId, positions, workingOrders };
  });
}

/** Nothing open and nothing working in scope. */
export function isFlat(exposure: readonly AccountExposure[]): boolean {
  return exposure.every((e) => e.positions.length === 0 && e.workingOrders === 0);
}

const signed = (n: number): string => (n > 0 ? `+${String(n)}` : `−${String(-n)}`);

/** "A1 NQZ6 +2, A2 ESZ6 −1 · 1 working order", or "" when flat. */
export function describeExposure(exposure: readonly AccountExposure[]): string {
  const pos = exposure.flatMap((e) =>
    e.positions.map((p) => `${p.accountId} ${p.symbol} ${signed(p.netQty)}`),
  );
  const working = exposure.reduce((n, e) => n + e.workingOrders, 0);
  const parts: string[] = [];
  if (pos.length > 0) parts.push(pos.join(", "));
  if (working > 0) parts.push(`${String(working)} working order${working === 1 ? "" : "s"}`);
  return parts.join(" · ");
}
